/**
 * 🚀 HERO SECTION - Station Spatiale La Petite Épicerie
 * Logo flottant, titre épique, horaires en hologramme
 */

import React from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Clock, MapPin, Truck } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_loyaltyhub-3/artifacts/bd853roq_La%20petite%20e%CC%81picerie-logo.png";

const HeroSection = ({ onScrollDown }) => {
  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center px-4 py-8 overflow-x-hidden overflow-y-auto">
      {/* Cercles orbitaux autour du logo */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <motion.div
          className="absolute w-[400px] h-[400px] rounded-full border border-cyan-500/20"
          animate={{ rotate: 360 }}
          transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
        />
        <motion.div
          className="absolute w-[500px] h-[500px] rounded-full border border-purple-500/10"
          animate={{ rotate: -360 }}
          transition={{ duration: 80, repeat: Infinity, ease: "linear" }}
        />
        <motion.div
          className="absolute w-[600px] h-[600px] rounded-full border border-pink-500/5"
          animate={{ rotate: 360 }}
          transition={{ duration: 100, repeat: Infinity, ease: "linear" }}
        />
      </div>

      {/* Logo Flottant Épique */}
      <motion.div
        className="relative z-10 mb-8"
        initial={{ opacity: 0, scale: 0.5, y: -50 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 1, type: "spring", bounce: 0.4 }}
      >
        {/* Halo derrière le logo */}
        <motion.div
          className="absolute -inset-10 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(6, 182, 212, 0.3) 0%, rgba(139, 92, 246, 0.1) 50%, transparent 70%)'
          }}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.5, 0.8, 0.5]
          }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
        
        <motion.img 
          src={LOGO_URL} 
          alt="La Petite Épicerie" 
          className="relative h-32 md:h-44 lg:h-52 w-auto mx-auto"
          style={{ 
            filter: 'drop-shadow(0 0 40px rgba(6, 182, 212, 0.8)) drop-shadow(0 0 80px rgba(139, 92, 246, 0.5))'
          }}
          animate={{
            y: [0, -10, 0],
            rotate: [0, 1, -1, 0]
          }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          data-testid="hero-logo"
        />

        {/* Badge "STATION SPATIALE" */}
        <motion.div
          className="absolute -bottom-4 left-1/2 -translate-x-1/2 whitespace-nowrap"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <span 
            className="px-4 py-1.5 rounded-full text-[10px] md:text-xs font-mono tracking-[0.2em] uppercase"
            style={{
              background: 'rgba(6, 182, 212, 0.1)',
              border: '1px solid rgba(6, 182, 212, 0.3)',
              color: '#06b6d4',
              boxShadow: '0 0 20px rgba(6, 182, 212, 0.3)'
            }}
          >
            Station Spatiale • Vermelles
          </span>
        </motion.div>
      </motion.div>

      {/* Titre Principal */}
      <motion.div
        className="text-center z-10 mb-8"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.8 }}
      >
        <motion.h1 
          className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tight"
          style={{
            background: 'linear-gradient(135deg, #06b6d4 0%, #8b5cf6 30%, #ec4899 60%, #06b6d4 100%)',
            backgroundSize: '200% 200%',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textShadow: '0 0 80px rgba(139, 92, 246, 0.5)'
          }}
          animate={{
            backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          data-testid="hero-title"
        >
          LA PETITE ÉPICERIE
        </motion.h1>

        <motion.p
          className="mt-4 text-lg md:text-xl text-gray-300 font-light"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          Votre épicerie de quartier, intergalactique
        </motion.p>
      </motion.div>

      {/* Panel Holographique - Infos Clés */}
      <motion.div
        className="relative z-10 w-full max-w-3xl mb-8"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.8 }}
      >
        <div 
          className="relative backdrop-blur-xl rounded-2xl p-6 overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)',
            border: '1px solid rgba(6, 182, 212, 0.2)',
            boxShadow: '0 0 40px rgba(6, 182, 212, 0.1), inset 0 0 60px rgba(6, 182, 212, 0.05)'
          }}
        >
          {/* Scan line effect */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            style={{ 
              background: 'linear-gradient(transparent 50%, rgba(6, 182, 212, 0.03) 50%)',
              backgroundSize: '100% 4px'
            }}
          />
          <motion.div
            className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent"
            animate={{ top: ['0%', '100%', '0%'] }}
            transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          />

          {/* Contenu */}
          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Horaires */}
            <div className="flex items-center gap-4 justify-center md:justify-start">
              <motion.div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.2) 0%, rgba(6, 182, 212, 0.1) 100%)',
                  border: '1px solid rgba(6, 182, 212, 0.3)'
                }}
                animate={{
                  boxShadow: ['0 0 10px rgba(6, 182, 212, 0.3)', '0 0 25px rgba(6, 182, 212, 0.6)', '0 0 10px rgba(6, 182, 212, 0.3)']
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Clock className="w-6 h-6 text-cyan-400" />
              </motion.div>
              <div>
                <p className="text-xs font-mono text-cyan-500/70 uppercase tracking-wider">Horaires</p>
                <p className="text-white font-bold">7j/7 • Midi - Minuit</p>
                <p className="text-cyan-300 text-xs">🌙 Ramadan: 21h-4h</p>
              </div>
            </div>

            {/* Livraison */}
            <div className="flex items-center gap-4 justify-center">
              <motion.div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(34, 197, 94, 0.1) 100%)',
                  border: '1px solid rgba(34, 197, 94, 0.3)'
                }}
                animate={{
                  boxShadow: ['0 0 10px rgba(34, 197, 94, 0.3)', '0 0 25px rgba(34, 197, 94, 0.6)', '0 0 10px rgba(34, 197, 94, 0.3)']
                }}
                transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
              >
                <Truck className="w-6 h-6 text-green-400" />
              </motion.div>
              <div>
                <p className="text-xs font-mono text-green-500/70 uppercase tracking-wider">Livraison</p>
                <motion.p 
                  className="text-white font-bold"
                  animate={{ color: ['#fff', '#22c55e', '#fff'] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  GRATUITE !
                </motion.p>
                <p className="text-green-300 text-xs">🌙 Ramadan: 21h-4h</p>
              </div>
            </div>

            {/* Localisation */}
            <div className="flex items-center gap-4 justify-center md:justify-end">
              <motion.div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.2) 0%, rgba(236, 72, 153, 0.1) 100%)',
                  border: '1px solid rgba(236, 72, 153, 0.3)'
                }}
                animate={{
                  boxShadow: ['0 0 10px rgba(236, 72, 153, 0.3)', '0 0 25px rgba(236, 72, 153, 0.6)', '0 0 10px rgba(236, 72, 153, 0.3)']
                }}
                transition={{ duration: 2, repeat: Infinity, delay: 1 }}
              >
                <MapPin className="w-6 h-6 text-pink-400" />
              </motion.div>
              <div>
                <p className="text-xs font-mono text-pink-500/70 uppercase tracking-wider">Coordonnées</p>
                <p className="text-white font-bold text-sm">138 Rue A. Lamendin</p>
                <p className="text-pink-300 text-xs">62980 Vermelles</p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.button
        onClick={onScrollDown}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-gray-500 hover:text-cyan-400 transition-colors"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        data-testid="scroll-indicator"
      >
        <span className="text-xs font-mono tracking-wider uppercase">Explorer</span>
        <ChevronDown className="w-6 h-6" />
      </motion.button>

      {/* Corner HUD elements */}
      <div className="absolute top-4 left-4 w-16 h-16 border-t-2 border-l-2 border-cyan-500/30 rounded-tl-lg" />
      <div className="absolute top-4 right-4 w-16 h-16 border-t-2 border-r-2 border-cyan-500/30 rounded-tr-lg" />
      <div className="absolute bottom-4 left-4 w-16 h-16 border-b-2 border-l-2 border-purple-500/30 rounded-bl-lg" />
      <div className="absolute bottom-4 right-4 w-16 h-16 border-b-2 border-r-2 border-purple-500/30 rounded-br-lg" />
    </section>
  );
};

export default HeroSection;
